import React, { useEffect, useState, useCallback } from "react";
import { api, arabicError } from "../lib/api";
import { toast } from "sonner";
import { Image as ImageIcon, Trash2, ToggleLeft, ToggleRight, BarChart3, Pencil, Bell } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import PhotoDashboardModal from "../components/PhotoDashboardModal";
import EditPhotoModal from "../components/EditPhotoModal";
import MerchantPhotoThumb from "../components/MerchantPhotoThumb";
import { PRODUCT_TYPES, PRODUCT_CATEGORIES, COUNTRIES as ALL_COUNTRIES, unitDisplay } from "../lib/constants";

const CATEGORIES = ["", ...PRODUCT_CATEGORIES];
const TYPES = ["", ...PRODUCT_TYPES];
const COUNTRIES = ["", ...ALL_COUNTRIES];

const fmt = (v) => `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Merchant-private accounting numbers (owner endpoint only — never returned to employees/public).
const ProductMoney = ({ p, m }) => {
  const u = unitDisplay(p.unit).short;
  const cost = p.cost_usd > 0 ? p.cost_usd : null;
  const rev = m?.net_revenue ?? 0;
  const profitKnown = cost !== null && m && m.profit_complete;
  return (
    <div className="mt-2 border border-[#DCEBEE] rounded-lg p-2 text-xs grid grid-cols-2 gap-x-2 gap-y-1.5" data-testid={`product-money-${p.id}`}>
      <div><div className="text-[#777777]">رأس المال / {u}</div><div className={`font-extrabold ${cost ? "text-[#333333]" : "text-amber-600"}`} dir="ltr">{cost ? fmt(cost) : "غير محدد"}</div></div>
      <div><div className="text-[#777777]">صافي الإيراد</div><div className="font-extrabold text-[#09637E]" dir="ltr">{fmt(rev)}</div></div>
      <div><div className="text-[#777777]">عمليات البيع</div><div className="font-extrabold text-[#333333]">{m?.sales_count || 0}{m?.returned_qty > 0 ? <span className="text-rose-600 font-normal"> · مرتجع {m.returned_qty} {u}</span> : null}</div></div>
      <div><div className="text-[#777777]">الربح</div><div className={`font-extrabold ${profitKnown ? (m.profit >= 0 ? "text-emerald-700" : "text-rose-600") : "text-amber-600"}`} dir="ltr" data-testid={`product-profit-${p.id}`}>{profitKnown ? fmt(m.profit) : (cost ? "—" : "أضف التكلفة")}</div></div>
    </div>
  );
};

// Store-level low-stock threshold (owner only). Per-product override lives in the Edit modal.
const ThresholdControl = ({ value, onSaved, mixedUnits }) => {
  const [v, setV] = useState(value ?? "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setV(value ?? ""); }, [value]);
  const save = async () => {
    const n = parseFloat(v);
    if (!isFinite(n) || n < 0) { toast.error("أدخل رقماً صالحاً"); return; }
    setBusy(true);
    try { const r = await api.put("/merchant/low-stock-threshold", { threshold: n }); toast.success(r.data.message); onSaved(n); }
    catch (e) { toast.error(arabicError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2 bg-white border border-[#7AB2B2] rounded-xl px-3 h-12" data-testid="low-stock-threshold-control">
      <Bell size={16} className="text-amber-600 shrink-0" />
      <span className="text-xs md:text-sm font-bold text-[#333333] whitespace-nowrap">تنبيه المخزون عند ≤</span>
      <input type="number" min="0" step="1" value={v} onChange={(e) => setV(e.target.value)} className="w-16 h-8 rounded-md border border-[#7AB2B2] px-2 text-sm text-center" data-testid="low-stock-threshold-input" />
      <span className="text-xs text-[#666666]" data-testid="low-stock-threshold-unit">{mixedUnits ? "وحدة" : "م"}</span>
      <button onClick={save} disabled={busy || String(v) === String(value ?? "")} className="h-8 px-3 rounded-md bg-[#09637E] text-white text-xs font-bold disabled:opacity-40" data-testid="low-stock-threshold-save">حفظ</button>
    </div>
  );
};

const StockBadge = ({ avail, threshold, testid, unit }) => {
  if (avail <= 0) return <span className="absolute top-2 right-2 px-2 py-1 rounded text-xs font-bold bg-red-600 text-white" data-testid={testid}>نفدت الكمية</span>;
  if (avail <= threshold) return <span className="absolute top-2 right-2 px-2 py-1 rounded text-xs font-bold bg-amber-500 text-white" data-testid={testid}>مخزون منخفض · {avail} {unitDisplay(unit).short}</span>;
  return <span className="absolute top-2 right-2 px-2 py-1 rounded text-xs font-bold bg-green-500 text-white" data-testid={testid}>متوفر</span>;
};

const MyPhotos = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ category: "", type: "", country: "" });
  const [dashboardPhotoId, setDashboardPhotoId] = useState(null);
  const [viewImage, setViewImage] = useState(null);
  const [editPhoto, setEditPhoto] = useState(null);
  const [metrics, setMetrics] = useState({});
  const [threshold, setThreshold] = useState(null);
  const [sp] = useSearchParams();
  const highlight = sp.get("highlight");
  useEffect(() => { api.get("/merchant/low-stock-threshold").then((r) => setThreshold(r.data.threshold)).catch(() => setThreshold(10)); }, []);
  useEffect(() => {
    if (!highlight || loading) return;
    const el = document.getElementById(`photo-card-${highlight}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlight, loading]);
  const loadMetrics = useCallback(() => { api.get("/accounting/products/metrics").then((r) => setMetrics(r.data || {})).catch(() => {}); }, []);
  useEffect(() => { loadMetrics(); }, [loadMetrics]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await api.get("/photos/mine", { params });
      setPhotos(r.data);
    } catch (e) { toast.error(arabicError(e)); } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (id) => {
    try { await api.patch(`/photos/${id}/status`); load(); }
    catch (e) { toast.error(arabicError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذه الصورة؟")) return;
    try { await api.delete(`/photos/${id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(arabicError(e)); }
  };

  const selectCls = "h-11 rounded-lg border border-[#7AB2B2] bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#09637E]";

  return (
    <div className="space-y-6" data-testid="my-photos-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-[#333333]">صور منتجاتي</h1>
          <p className="text-[#666666] mt-1">{photos.length} منتج في مخزونك</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {threshold !== null && <ThresholdControl value={threshold} onSaved={setThreshold} mixedUnits={photos.some((p) => p.unit && p.unit !== "meter")} />}
          <Link to="/upload" data-testid="goto-upload"
            className="h-12 px-5 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold flex items-center gap-2">
            + رفع صورة
          </Link>
        </div>
      </div>

      {/* Motivational banner */}
      <div className="bg-[#EBF4F6] border border-[#7AB2B2] rounded-xl p-4 text-[#09637E] font-bold text-center text-sm md:text-base" data-testid="motivational-banner">
        كل صورة ترفعها تزيد فرصة بيع بضائعك. واصل الإضافة!
      </div>

      <div className="bg-white border border-[#7AB2B2] rounded-xl p-4 flex flex-wrap gap-3">
        <select className={selectCls} value={filters.category} onChange={(e) => setFilters((s) => ({ ...s, category: e.target.value }))} data-testid="filter-category">
          {CATEGORIES.map((c) => <option key={c} value={c}>{c || "كل الأصناف"}</option>)}
        </select>
        <select className={selectCls} value={filters.type} onChange={(e) => setFilters((s) => ({ ...s, type: e.target.value }))} data-testid="filter-type">
          {TYPES.map((c) => <option key={c} value={c}>{c || "كل الأنواع"}</option>)}
        </select>
        <select className={selectCls} value={filters.country} onChange={(e) => setFilters((s) => ({ ...s, country: e.target.value }))} data-testid="filter-country">
          {COUNTRIES.map((c) => <option key={c} value={c}>{c || "كل البلدان"}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="bg-white border border-[#7AB2B2] rounded-xl p-8 text-center text-[#666666]">جارٍ التحميل...</div>
      ) : photos.length === 0 ? (
        <div className="bg-white border border-[#7AB2B2] rounded-xl p-12 text-center" data-testid="empty-my-photos">
          <ImageIcon className="mx-auto text-[#7AB2B2] mb-3" size={48} />
          <p className="text-[#666666] text-lg mb-4">لا توجد صور مطابقة</p>
          <Link to="/upload" className="inline-block h-12 px-6 leading-[3rem] rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold">رفع أول صورة</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {photos.map((p) => {
            const avail = p.quantity_available ?? p.quantity ?? 0;
            const sold = p.quantity_sold ?? 0;
            const price = p.price_usd ?? p.price ?? 0;
            const ud = unitDisplay(p.unit);                                   // Phase 5C-1: labels only; legacy (no unit) → م
            const outOfStock = p.status === "out_of_stock" || avail <= 0;
            const effThreshold = (p.low_stock_threshold ?? null) !== null ? p.low_stock_threshold : (threshold ?? 10);
            return (
            <div key={p.id} id={`photo-card-${p.id}`} className={`bg-white border rounded-xl overflow-hidden flex flex-col ${highlight === p.id ? "border-amber-500 ring-2 ring-amber-300" : "border-[#7AB2B2]"}`} data-testid={`photo-card-${p.id}`}>
              <div className="relative">
                <div className="w-full h-48">
                  <MerchantPhotoThumb
                    photoId={p.id}
                    alt={p.display_name || ""}
                    className="cursor-zoom-in"
                    onClick={(src) => setViewImage({ ...p, image_url: src })}
                    testid={`photo-open-${p.id}`}
                  />
                </div>
                <StockBadge avail={avail} threshold={effThreshold} testid={`stock-badge-${p.id}`} unit={p.unit} />
                {p.reserved_quantity > 0 && <span className="absolute bottom-2 right-2 text-[11px] font-extrabold px-2 py-0.5 rounded-full bg-amber-500 text-white shadow" data-testid={`reserved-badge-${p.id}`}>محجوز: {p.reserved_quantity} {ud.short}</span>}
              </div>
              <div className="p-4 flex-1 flex flex-col gap-1">
                <div className="font-bold text-[#333333] truncate" title={p.display_name}>{p.display_name || `${p.size} · ${p.color || ""}`}</div>
                <div className="text-sm text-[#666666]">{p.category} · {p.type} · {p.country}</div>
                <div className="text-xs text-[#666666]">{p.manufacturer || "—"}</div>
                {p.uploaded_by?.actor_type === "employee" && (
                  <div className="text-[11px] text-[#666666]" data-testid={`uploaded-by-${p.id}`}>رفعه الموظف: <b>{p.uploaded_by.actor_name}</b></div>
                )}
                <div className="text-xs font-bold text-[#09637E] bg-[#EBF4F6] rounded px-2 py-1 w-fit" data-testid={`product-code-${p.id}`}>
                  كود المنتج: <span dir="ltr" className="font-mono">{p.product_code || "—"}</span>
                </div>
                <div className="text-xl font-extrabold text-[#09637E] mt-1" data-testid={`photo-price-${p.id}`}>${price.toFixed(2)} / {ud.short}</div>
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                  <div className="bg-[#EBF4F6] rounded-lg p-2">
                    <div className="text-[#666666]">المتوفر</div>
                    <div className="font-extrabold text-[#09637E]" data-testid={`photo-avail-${p.id}`}>{avail} {ud.short}</div>
                  </div>
                  <div className="bg-[#EBF4F6] rounded-lg p-2">
                    <div className="text-[#666666]">المباع</div>
                    <div className="font-extrabold text-green-700">{sold} {ud.short}</div>
                  </div>
                </div>
                <ProductMoney p={p} m={metrics[p.id]} />
                <div className="flex gap-2 mt-3">
                  <button onClick={() => setEditPhoto(p)} data-testid={`edit-photo-${p.id}`}
                    className="flex-1 h-10 rounded-lg bg-[#088395] hover:bg-[#09637E] text-white font-bold text-sm flex items-center justify-center gap-1">
                    <Pencil size={16} /> تعديل
                  </button>
                  <button onClick={() => setDashboardPhotoId(p.id)} data-testid={`photo-dashboard-${p.id}`}
                    className="flex-1 h-10 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold text-sm flex items-center justify-center gap-1">
                    <BarChart3 size={16} /> اللوحة
                  </button>
                </div>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => toggle(p.id)} data-testid={`toggle-status-${p.id}`}
                    className="flex-1 h-10 rounded-lg border-2 border-[#09637E] text-[#09637E] hover:bg-[#EBF4F6] font-bold text-sm flex items-center justify-center gap-1">
                    {!outOfStock ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                    {!outOfStock ? "متوفر" : "نفد"}
                  </button>
                  <button onClick={() => del(p.id)} data-testid={`delete-photo-${p.id}`}
                    className="h-10 px-3 rounded-lg border-2 border-red-500 text-red-500 hover:bg-red-50 font-bold">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {dashboardPhotoId && (
        <PhotoDashboardModal photoId={dashboardPhotoId} onClose={() => setDashboardPhotoId(null)} />
      )}

      {viewImage && (
        <div
          className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4"
          onClick={() => setViewImage(null)}
          data-testid="image-lightbox"
        >
          <button
            onClick={() => setViewImage(null)}
            className="absolute top-4 left-4 w-11 h-11 rounded-full bg-white/90 hover:bg-white flex items-center justify-center text-[#333333] font-extrabold text-xl"
            aria-label="إغلاق"
          >✕</button>
          <img
            src={viewImage.image_url}
            alt={viewImage.display_name || ""}
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-4 inset-x-4 text-center text-white text-sm md:text-base font-bold bg-black/50 rounded-lg py-2 px-4 max-w-2xl mx-auto">
            {viewImage.display_name || `${viewImage.category} · ${viewImage.type} · ${viewImage.country} · ${viewImage.size}`}
          </div>
        </div>
      )}

      {editPhoto && (
        <EditPhotoModal photo={editPhoto} onClose={() => setEditPhoto(null)} onSaved={() => { load(); loadMetrics(); }} />
      )}
    </div>
  );
};

export default MyPhotos;
